import { BulkVisitsActionBar } from '@/components/bulkQueue/BulkVisitsActionBar';
import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderNotesRenderCell, OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import { ClinicianRenderCell } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import {
    ContextServicesSelectionCell,
    ServicesSelectionHeader,
} from '@/components/bulkQueue/cells/services/ServicesSelection';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { BulkServicesAssignmentTableQuery } from '@/components/bulkQueue/tables/services/BulkServicesAssignmentTable.gql.types';
import {
    BulkServicesContextProvider,
    useBulkServicesContext,
} from '@/components/bulkQueue/tables/services/BulkServicesContext';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { ServicesTableRow, ServicesVisitRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { useReassignVisitPermissionsFilter } from '@/components/visits/dialogs/ReassignVisits/reassignVisitPermissions';
import { EntrySource } from '@/components/visits/dialogs/ReassignVisits/v3/tracking/entrySource';
import { VisitColumns } from '@/components/visits/tables/columns';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { useHasVisitCancelPermission } from '@/hooks/permissions/visit';
import { FeatureFlag } from '@/types/featureFlag';
import { VisitState } from '@/types/visit';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { Checkbox, CheckboxProps, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

const servicesAssignmentVisit = gql`
    fragment ServicesAssignmentVisit on Visit {
        availableServices {
            id
            name
            isVirtual
        }
    }
`;

export const BULK_SERVICES_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkServicesAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
    ) {
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering) {
            totalCount
            items {
                ...VisitTableRow
                ...ServicesAssignmentVisit
            }
        }
    }
    ${VisitTableRow}
    ${servicesAssignmentVisit}
`;

type BulkServicesAssignmentTableProps = {
    rows: ServicesTableRow[];
    pagination: Pagination;
    title?: string;
    serverSideSort: ServerSideSort;
    showSelection: boolean;
    selectedRowValues: string[];
    setSelectedRowValues: (values: string[]) => void;
};

const rowToSelectionId = (row: ServicesTableRow) => row.id;

export const BulkServicesAssignmentTable: FC<BulkServicesAssignmentTableProps> = (props) => {
    const {
        rows,
        pagination,
        title,
        serverSideSort,
        showSelection,
        selectedRowValues,
        setSelectedRowValues,
    } = props;
    const selection = useBulkServicesContext();
    const hasOrders = useFeatureFlagEnabled(FeatureFlag.ORDERS);
    const hasEditableItems = rows.some(
        (r) => r.__typename !== 'Visit' || r.permissions.editServices,
    );
    // Row selection and the in-cell service picker carry independent pending
    // state, so each locks while the other is in use.
    const hasServiceSelections = selection.selectedCount > 0;
    const isLocked = selectedRowValues.length > 0;
    // Hide row selection (and its select-all header checkbox) when there are no
    // rows to select. AxleTable requires all selection props to agree.
    const showBulkSelect = showSelection && rows.length > 0;

    const disabledCheckboxComponents = useMemo(
        () => ({
            Checkbox: (props: CheckboxProps) => <Checkbox {...props} disabled />,
        }),
        [],
    );

    const columns: RawColumn<ServicesTableRow>[] = useMemo(
        () => [
            {
                field: 'sortPriority',
                label: 'Priority',
                rowToComponent: (entity) => <PriorityRenderCell entity={entity} />,
                options: { ...VisitColumns.priorityColumnOptions, sort: false },
            },
            {
                field: 'patient.fullName',
                label: 'Patient',
                rowToComponent: (entity) => <PatientRenderCell entity={entity} />,
            },
            {
                field: 'sortServicesName',
                label: 'Services',
                rowToComponent: (entity) =>
                    entity.__typename === 'Visit' && !entity.permissions.editServices ? (
                        <ServicesRenderCell entity={entity} />
                    ) : (
                        <ContextServicesSelectionCell entity={entity} isLocked={isLocked} />
                    ),
                options: {
                    sort: false,
                    setCellProps: (_value, rowIndex) => {
                        const row = rows[rowIndex];
                        const isEditable =
                            row.__typename !== 'Visit' || row.permissions.editServices;
                        return getSetCellProps(
                            hasEditableItems && isEditable
                                ? selection.selectedCount === 0 ||
                                      selection.getSelectionState(rows[rowIndex]).isSelected
                                : null,
                        );
                    },
                    customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                        <ServicesSelectionHeader
                            key={columnMeta.name + '-header'}
                            columnMeta={columnMeta}
                            handleToggleColumn={handleToggleColumn}
                            sortOrder={sortOrder}
                            hasEditableItems={hasEditableItems}
                            isLocked={isLocked}
                        />
                    ),
                },
            },
            {
                field: 'cliniciansSortKey',
                label: 'Clinician',
                rowToComponent: (entity) => <ClinicianRenderCell entity={entity} />,
                options: { sort: false },
            },
            {
                field: 'sortDate',
                label: 'Date',
                rowToComponent: (entity) => <DateRenderCell entity={entity} />,
            },
            {
                field: 'fulfillmentWindow.startDate',
                label: 'Fulfillment Window',
                rowToComponent: (entity) => <FulfillmentWindowRenderCell entity={entity} />,
            },
            ...(hasOrders
                ? [
                      {
                          field: 'orderSortKey',
                          label: 'Order',
                          rowToComponent: (entity: ServicesTableRow) => (
                              <OrderRenderCell entity={entity} />
                          ),
                          options: { sort: false },
                      },
                      {
                          label: 'Order Notes',
                          rowToComponent: (entity: ServicesTableRow) => (
                              <OrderNotesRenderCell entity={entity} />
                          ),
                      },
                  ]
                : []),
            {
                field: '',
                label: 'Actions',
                rowToComponent: (entity) => <ActionsRenderCell entity={entity} />,
            },
        ],
        [rows, selection, hasEditableItems, hasOrders, isLocked],
    );

    return (
        <AxleTable
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            toolbar={false}
            dynamicPaging={pagination}
            data={rows}
            rowToLink={rowToLink}
            columns={columns}
            serverSideSort={serverSideSort}
            selectableRows={showBulkSelect ? 'multiple' : 'none'}
            rowToSelectionIdentifier={showBulkSelect ? rowToSelectionId : undefined}
            selectedRowValues={showBulkSelect ? selectedRowValues : undefined}
            setSelectedRowValues={showBulkSelect ? setSelectedRowValues : undefined}
            components={hasServiceSelections ? disabledCheckboxComponents : undefined}
        />
    );
};

type ConnectedBulkServicesAssignmentTableProps = {
    title?: string;
};

type BulkActionsBarProps = {
    rows: ServicesTableRow[];
    selectedRowValues: string[];
    hasBulkReassign: boolean;
    hasBulkCancel: boolean;
};

const BulkActionsBar: FC<BulkActionsBarProps> = ({
    rows,
    selectedRowValues,
    hasBulkReassign,
    hasBulkCancel,
}) => {
    const selectedSet = useMemo(() => new Set(selectedRowValues), [selectedRowValues]);
    const reassignVisitFilter = useReassignVisitPermissionsFilter();

    const reassign = useMemo(() => {
        if (!hasBulkReassign) {
            return undefined;
        }
        const visitIds: string[] = [];
        rows.forEach((row) => {
            if (!selectedSet.has(row.id) || row.__typename === 'VisitRequest') {
                return;
            }
            if (!reassignVisitFilter || reassignVisitFilter(row.permissions)) {
                visitIds.push(row.id);
            }
        });
        if (visitIds.length === 0) {
            return undefined;
        }
        return {
            visitIds,
            entrySource: EntrySource.BulkServicesAssignmentTable,
        };
    }, [hasBulkReassign, rows, selectedSet, reassignVisitFilter]);

    const cancellableVisits = useMemo(
        () =>
            rows
                .filter(
                    (row) =>
                        row.__typename === 'Visit' &&
                        selectedSet.has(row.id) &&
                        row.state !== VisitState.ENDED &&
                        row.permissions.cancel,
                )
                .map((row) => ({
                    visitId: row.id,
                    patientId: row.patient.id,
                    patientName: row.patient.fullName,
                    city: row.address?.city,
                })),
        [rows, selectedSet],
    );

    return (
        <BulkVisitsActionBar
            selectedCount={selectedRowValues.length}
            cancellableVisits={cancellableVisits}
            showCancel={hasBulkCancel}
            showReassign={hasBulkReassign}
            reassign={reassign}
        />
    );
};

export const ConnectedBulkServicesAssignmentTable: FC<ConnectedBulkServicesAssignmentTableProps> = (
    props,
) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } = useConnectedBulkTable<
        BulkServicesAssignmentTableQuery,
        ServicesVisitRow
    >({ query: BULK_SERVICES_ASSIGNMENT_TABLE_QUERY });
    const hasBulkReassign = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_BULK_VISIT_REASSIGNMENT);
    const bulkCancelEnabled = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_BULK_CANCELLATION_ALL_VISITS,
    );
    const hasBulkCancel = useHasVisitCancelPermission() && bulkCancelEnabled;
    const hasBulkActions = hasBulkReassign || hasBulkCancel;
    const [selectedRowValues, setSelectedRowValues] = useState<string[]>([]);

    useEffect(() => {
        const rowIds = new Set(rows.map((r) => r.id));
        setSelectedRowValues((prev) => {
            const filtered = prev.filter((id) => rowIds.has(id));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [rows]);

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkServicesContextProvider entities={rows}>
            {hasBulkActions && (
                <BulkActionsBar
                    rows={rows}
                    selectedRowValues={selectedRowValues}
                    hasBulkReassign={hasBulkReassign}
                    hasBulkCancel={hasBulkCancel}
                />
            )}
            <BulkServicesAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
                showSelection={hasBulkActions}
                selectedRowValues={selectedRowValues}
                setSelectedRowValues={setSelectedRowValues}
            />
        </BulkServicesContextProvider>
    );
};
